/**
 * JourneyGlyph — 3-line abstract signature for a journey card.
 *
 * One inert SVG per sector. KATLAS-blue strokes (#7FB2FF) with a single
 * amber accent dot (#C19A4A) for emphasis. Pure SVG — no third-party
 * assets, no imagery, no emoji, no glyph fonts. Sizes to ~88×40 by
 * default; the consumer controls placement.
 *
 * Not a literal icon — the marks are intentionally minimal and indicative,
 * so the Journey Storybook reads as a curated anthology rather than a
 * grid of generic icons.
 */

const COMMON = {
  stroke: "#7FB2FF",
  accent: "#C19A4A",
};

const Wrap = ({ children, "aria-label": ariaLabel, testid }) => (
  <svg
    data-testid={testid}
    viewBox="0 0 88 40"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={ariaLabel}
    width="88"
    height="40"
    fill="none"
  >
    {children}
  </svg>
);

const SupplyChainGlyph = () => (
  <Wrap aria-label="Supply chain — node, asset in transit, node" testid="glyph-supply-chain">
    <circle cx="8" cy="20" r="5" stroke={COMMON.stroke} strokeWidth="1" />
    <line x1="13" y1="20" x2="40" y2="20" stroke={COMMON.stroke} strokeWidth="1" />
    <rect x="40" y="15" width="10" height="10" stroke={COMMON.stroke} strokeWidth="1" />
    <circle cx="45" cy="20" r="1.2" fill={COMMON.accent} />
    <line x1="50" y1="20" x2="74" y2="20" stroke={COMMON.stroke} strokeWidth="1" strokeDasharray="2 2" />
    <path d="M 74 20 L 80 20 M 76 17 L 80 20 L 76 23" stroke={COMMON.stroke} strokeWidth="1" />
  </Wrap>
);

const MobilityGlyph = () => (
  <Wrap aria-label="Mobility — node trail with directional arrow" testid="glyph-mobility">
    <circle cx="8" cy="30" r="1.5" fill={COMMON.stroke} />
    <circle cx="22" cy="26" r="1.5" fill={COMMON.stroke} />
    <circle cx="36" cy="22" r="1.5" fill={COMMON.stroke} />
    <circle cx="50" cy="18" r="1.5" fill={COMMON.stroke} />
    <circle cx="64" cy="14" r="2" fill={COMMON.accent} />
    <path d="M 64 14 L 80 14 M 76 11 L 80 14 L 76 17" stroke={COMMON.stroke} strokeWidth="1" />
  </Wrap>
);

const CitiesResilienceGlyph = () => (
  <Wrap aria-label="Cities and resilience — overlapping agencies inside a shared perimeter" testid="glyph-cities">
    <rect x="4" y="6" width="80" height="28" stroke={COMMON.stroke} strokeWidth="1" strokeDasharray="3 2" />
    <circle cx="28" cy="20" r="6" stroke={COMMON.stroke} strokeWidth="1" />
    <circle cx="44" cy="20" r="6" stroke={COMMON.stroke} strokeWidth="1" />
    <circle cx="60" cy="20" r="6" stroke={COMMON.stroke} strokeWidth="1" />
    <circle cx="44" cy="20" r="1.5" fill={COMMON.accent} />
  </Wrap>
);

const HealthGlyph = () => (
  <Wrap aria-label="Health — pulse line crossing a boundary point" testid="glyph-health">
    <line x1="4" y1="20" x2="22" y2="20" stroke={COMMON.stroke} strokeWidth="1" />
    <path d="M 22 20 L 30 8 L 38 32 L 46 12 L 54 20" stroke={COMMON.stroke} strokeWidth="1" />
    <line x1="54" y1="20" x2="84" y2="20" stroke={COMMON.stroke} strokeWidth="1" />
    <circle cx="54" cy="20" r="2" fill={COMMON.accent} />
  </Wrap>
);

const CrossSectorGlyph = () => (
  <Wrap aria-label="Cross-sector — custody, authority and receipts as a small triad" testid="glyph-cross-sector">
    <path d="M 16 30 L 44 8 L 72 30 Z" stroke={COMMON.stroke} strokeWidth="1" />
    <circle cx="16" cy="30" r="2" fill={COMMON.stroke} />
    <circle cx="44" cy="8" r="2" fill={COMMON.stroke} />
    <circle cx="72" cy="30" r="2" fill={COMMON.accent} />
  </Wrap>
);

const REGISTRY = {
  "Supply Chain": SupplyChainGlyph,
  "Mobility": MobilityGlyph,
  "Cities & Resilience": CitiesResilienceGlyph,
  "Health": HealthGlyph,
};

export const JourneyGlyph = ({ sector }) => {
  const Glyph = REGISTRY[sector] || CrossSectorGlyph;
  return <Glyph />;
};

export default JourneyGlyph;
